(() => {
  const STORAGE_PREFIX = 'tyzo_tabs_selected::';

  const getHashTabKey = () => {
    const hash = window.location.hash || '';
    const m = hash.match(/tab=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : '';
  };

  const getScopeRoot = (tabsRoot) => {
    const sel = (tabsRoot.getAttribute('data-tabs-scope') || '').trim();
    if (sel) {
      const el = document.querySelector(sel);
      if (el) return el;
    }
    return document;
  };

  const findTargets = (scopeRoot) => {
    const markers = scopeRoot.querySelectorAll('.js-tab-section-marker[data-tab-key]');
    const targets = [];
    markers.forEach((mk) => {
      const key = (mk.getAttribute('data-tab-key') || '').trim();
      if (!key) return;
      const wrapper = mk.closest('[id^="shopify-section-"]');
      if (!wrapper) return;
      targets.push({ key, wrapper });
    });
    return targets;
  };

  const setActiveTabButtonState = (buttons, activeKey) => {
    buttons.forEach((btn) => {
      const k = (btn.getAttribute('data-tab-key') || '').trim();
      const isActive = k === activeKey;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      btn.setAttribute('tabindex', isActive ? '0' : '-1');
    });
  };

  const applyVisibility = (targets, activeKey) => {
    targets.forEach(({ key, wrapper }) => {
      const show = key === activeKey;
      wrapper.classList.toggle('is-tab-hidden', !show);
      wrapper.setAttribute('data-tab-visible', show ? 'true' : 'false');
    });
  };

  const getDefaultKey = (tabsRoot, buttons) => {
    const fromSetting = (tabsRoot.getAttribute('data-tabs-default') || '').trim();
    if (fromSetting) return fromSetting;
    const first = buttons[0];
    return first ? (first.getAttribute('data-tab-key') || '').trim() : '';
  };

  const smoothScrollTo = (y) => {
    try {
      window.scrollTo({ top: y, behavior: 'smooth' });
    } catch (e) {
      window.scrollTo(0, y);
    }
  };

  // スクロールを1回にまとめて予約（体感の鈍さを減らす）
  let scrollRafId = 0;
  const requestSmoothScrollTo = (getY) => {
    if (scrollRafId) cancelAnimationFrame(scrollRafId);
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = 0;
        const y = typeof getY === 'function' ? getY() : getY;
        smoothScrollTo(Math.max(0, y));
    });
  };

  const initTabs = (tabsRoot) => {
    const rootId = tabsRoot.getAttribute('data-tabs-root-id') || '';
    const scopeRoot = getScopeRoot(tabsRoot);

    const buttons = Array.from(tabsRoot.querySelectorAll('[role="tab"][data-tab-key]'));
    if (!buttons.length) return;

    const targets = findTargets(scopeRoot);
    const storageKey = STORAGE_PREFIX + (rootId || 'default');

    // Collapse / More settings (from Liquid data-attrs)
    const moreBtn = tabsRoot.querySelector('[data-tabs-more]');
    const collapseHeight = parseInt(tabsRoot.getAttribute('data-tabs-collapse-height') || '300', 10);

    // 追加（2026/02/26：テーマエディターの不具合対応）
    // ===== Theme Editor / hot reload: purge duplicated "more" buttons =====
    const esc = (v) => (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/"/g, '\\"');

    const purgeMoreButtons = () => {
      if (!moreBtn || !rootId) return;

      // 現在のボタンに owner を付与
      moreBtn.setAttribute('data-tabs-more-owner', rootId);

      // owner が同じボタンは「今の moreBtn 以外」全部消す（tabsRoot内外を問わない）
      document
        .querySelectorAll(`[data-tabs-more-owner="${esc(rootId)}"]`)
        .forEach((btn) => {
          if (btn !== moreBtn) btn.remove();
        });

      // 念のため：tabsRoot内の [data-tabs-more] が複数なら今の以外消す
      tabsRoot.querySelectorAll('[data-tabs-more]').forEach((btn) => {
        if (btn !== moreBtn) btn.remove();
      });
    };

    // init時に必ず1回 purge（設定変更で増殖するのを止める）
    purgeMoreButtons();

    const closeEnabled = (tabsRoot.getAttribute('data-tabs-close-enabled') || 'true') === 'true';
    const closeLabel = tabsRoot.getAttribute('data-tabs-close-label') || '閉じる';

    const scrollOnClose = (tabsRoot.getAttribute('data-tabs-scroll-on-close') || 'true') === 'true';
    const scrollOffset = parseInt(tabsRoot.getAttribute('data-tabs-scroll-offset') || '0', 10);

    let isExpanded = false;

    // Determine initial active key (priority: hash > storage > default)
    const fromHash = getHashTabKey();
    // const fromStorage = sessionStorage.getItem(storageKey) || '';
    const fromStorage = '';
    const defaultKey = getDefaultKey(tabsRoot, buttons);

    let activeKey = (fromHash || fromStorage || defaultKey).trim();

    // Validate activeKey exists among tabs
    const keys = buttons.map((b) => (b.getAttribute('data-tab-key') || '').trim());
    if (!keys.includes(activeKey)) activeKey = defaultKey;

    // Remember original button position (home)
    const moreBtnHome = moreBtn ? { parent: moreBtn.parentNode, next: moreBtn.nextSibling } : null;

    const restoreMoreBtnHome = () => {
      if (!moreBtn || !moreBtnHome || !moreBtnHome.parent) return;
      if (moreBtnHome.next && moreBtnHome.next.parentNode === moreBtnHome.parent) {
        moreBtnHome.parent.insertBefore(moreBtn, moreBtnHome.next);
      } else {
        moreBtnHome.parent.appendChild(moreBtn);
      }
    };

    const moveMoreBtnInto = (wrapper) => {
      if (!moreBtn || !wrapper) return;
      wrapper.appendChild(moreBtn);
    };

    // ---------- Collapse helpers ----------
    const resetTrim = () => {
      const all = scopeRoot.querySelectorAll(
        '[id^="shopify-section-"].is-tab-trimmed, [id^="shopify-section-"][data-tab-hidden-by-collapse="true"]'
      );
      all.forEach((w) => {
        w.classList.remove('is-tab-trimmed');
        w.style.maxHeight = '';
        w.style.overflow = '';
        if (w.getAttribute('data-tab-hidden-by-collapse') === 'true') {
          w.style.display = '';
          w.removeAttribute('data-tab-hidden-by-collapse');
        }
      });

      // Always restore button to home before re-applying
      restoreMoreBtnHome();
    };

    const getVisibleWrappersInActiveTab = () => {
      const list = [];
      targets.forEach(({ key, wrapper }) => {
        if (key === activeKey && wrapper.getAttribute('data-tab-visible') === 'true') {
          list.push(wrapper);
        }
      });

      // Sort by DOM order
      list.sort((a, b) => {
        const pos = a.compareDocumentPosition(b);
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (pos & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });

      return list;
    };

    const getLastWrapperInActiveTab = () => {
      const wrappers = getVisibleWrappersInActiveTab();
      return wrappers.length ? wrappers[wrappers.length - 1] : null;
    };

    const getActiveTabLabel = () => {
      const btn = buttons.find((b) => (b.getAttribute('data-tab-key') || '').trim() === activeKey);
      if (!btn) return '';
      return (btn.textContent || '').trim();
    };

    const setMoreBtnLabel = () => {
      if (!moreBtn) return;

      if (isExpanded) {
        if (closeEnabled) {
          moreBtn.hidden = false;
          moreBtn.textContent = closeLabel;
        } else {
          moreBtn.hidden = true;
        }
        moreBtn.setAttribute('aria-expanded', 'true');
        return;
      }

      // Collapsed: "（タブ名）を見る"
      const label = getActiveTabLabel();
      moreBtn.hidden = false;
      // moreBtn.textContent = label ? `${label}を見る` : 'もっと見る';
      moreBtn.textContent = 'もっと見る';
      moreBtn.setAttribute('aria-expanded', 'false');
    };

    const scrollBackToTabs = () => {
      if (!scrollOnClose) return;
      const rect = tabsRoot.getBoundingClientRect();
      const y = window.pageYOffset + rect.top + (Number.isFinite(scrollOffset) ? scrollOffset : 0);
      smoothScrollTo(Math.max(0, y));
    };

    const applyCollapse = () => {
      if (!moreBtn) return;

      purgeMoreButtons();  // 追加（2026/02/26：テーマエディターの不具合対応）
      resetTrim();

      const wrappers = getVisibleWrappersInActiveTab();

      // no targets
      if (!wrappers.length) {
        moreBtn.hidden = true;
        return;
      }

      // Expanded: show all + move close button to bottom of active tab
      if (isExpanded) {
        const last = getLastWrapperInActiveTab();
        if (last) moveMoreBtnInto(last);
        else restoreMoreBtnHome();

        setMoreBtnLabel();
        return;
      }

      // Collapsed: check if collapse needed
      const totalHeight = wrappers.reduce((sum, w) => sum + w.offsetHeight, 0);
      if (!(collapseHeight > 0) || totalHeight <= collapseHeight) {
        moreBtn.hidden = true;
        return;
      }

      // Need collapse -> show "(tab)を見る"
      setMoreBtnLabel();

      let used = 0;

      for (let i = 0; i < wrappers.length; i++) {
        const w = wrappers[i];
        const h = w.offsetHeight;

        if (used >= collapseHeight) {
          w.style.display = 'none';
          w.setAttribute('data-tab-hidden-by-collapse', 'true');
          continue;
        }

        if (used + h <= collapseHeight) {
          used += h;
          continue;
        }

        // Trim this section to remaining height
        const remaining = Math.max(0, collapseHeight - used);
        w.classList.add('is-tab-trimmed');
        w.style.maxHeight = `${remaining}px`;
        w.style.overflow = 'hidden';

        // Place the button near the boundary (inside trimmed wrapper)
        moveMoreBtnInto(w);

        used = collapseHeight;

        // Hide rest
        for (let j = i + 1; j < wrappers.length; j++) {
          wrappers[j].style.display = 'none';
          wrappers[j].setAttribute('data-tab-hidden-by-collapse', 'true');
        }
        break;
      }
    };

    // 高さ変化を監視して折りたたみを再計算（初回ズレ対策）
    let ro = null;
    let roScheduled = false;

    const scheduleRecalc = () => {
      if (roScheduled) return;
      roScheduled = true;
      requestAnimationFrame(() => {
        roScheduled = false;
        // activeKey / isExpanded の状態に合わせて再計算
        applyCollapse();
      });
    };

    const observeActiveWrappers = () => {
      if (!('ResizeObserver' in window)) return;

      if (ro) ro.disconnect();
      ro = new ResizeObserver(scheduleRecalc);

      const wrappers = getVisibleWrappersInActiveTab();
      wrappers.forEach((w) => ro.observe(w));
    };

    // ---------- Main control ----------
    const setActive = (nextKey, { updateHash = false, focusPanel = false, scrollToTabs = false } = {}) => {
      const key = (nextKey || '').trim();
      if (!key || !keys.includes(key)) return;

      activeKey = key;
      // sessionStorage.setItem(storageKey, activeKey);

      setActiveTabButtonState(buttons, activeKey);
      applyVisibility(targets, activeKey);

      isExpanded = false;

      // まず1回
      applyCollapse();

      // 高さ変化を監視
      observeActiveWrappers();

      // その後、表示→レイアウト確定を待ってもう一度（最初のズレ対策）
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          applyCollapse();

          // 2回目の計測後に監視を張り直す
          observeActiveWrappers();


          if (scrollToTabs) {
            requestSmoothScrollTo(() => {
              const rect = tabsRoot.getBoundingClientRect();
              return window.pageYOffset + rect.top + (Number.isFinite(scrollOffset) ? scrollOffset : 0);
            });
          }
        });
      });

      if (updateHash) {
        const newHash = `#tab=${encodeURIComponent(activeKey)}`;
        if (window.location.hash !== newHash) window.location.hash = newHash;
      }

      if (focusPanel) {
        const panel = tabsRoot.querySelector('[role="tabpanel"]');
        if (panel) panel.focus({ preventScroll: false });
      }

      document.dispatchEvent(new CustomEvent('tyzo:tabChanged', { detail: { activeKey, rootId } }));
    };

    // Click
    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = (btn.getAttribute('data-tab-key') || '').trim();
        setActive(key, { updateHash: true, focusPanel: false, scrollToTabs: true });
      });

      // Keyboard (left/right)
      btn.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();

        const idx = buttons.indexOf(btn);
        const nextIdx =
          e.key === 'ArrowRight'
            ? (idx + 1) % buttons.length
            : (idx - 1 + buttons.length) % buttons.length;

        const nextBtn = buttons[nextIdx];
        nextBtn.focus();

        const key = (nextBtn.getAttribute('data-tab-key') || '').trim();
        setActive(key, { updateHash: true, focusPanel: false, scrollToTabs: true });
      });
    });

    // More button click (toggle)
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        const wasExpanded = isExpanded;
        isExpanded = !isExpanded;

        applyCollapse();

        // If we just closed, scroll back to tab area
        if (wasExpanded && !isExpanded) {
          scrollBackToTabs();
        }

        document.dispatchEvent(
          new CustomEvent('tyzo:tabCollapsedChanged', { detail: { activeKey, rootId, isExpanded } })
        );
      });
    }

    // First apply
    setActive(activeKey, { updateHash: !!fromHash, focusPanel: false });
  };

  const boot = () => {
    document.querySelectorAll('[data-tabs-root]').forEach(initTabs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();